// Class B/C/D controlled airspace layer family.
//
// FAA Class B/C/D airspace — color-coded by CLASS property:
//   B → blue  (matches VFR sectional convention)
//   C → magenta  (matches VFR sectional convention)
//   D → blue dashed  (matches VFR sectional convention; dashed line)
// One GeoJSON source drives three layers: a low-opacity class-coded fill,
// a class-coded border line (Class D uses a dash pattern), and a symbol
// label at zoom ≥ 7. All layers are mounted with visibility: 'none'
// (lazy toggle). Hidden by default; Aero panel drives the toggle.
//
// Extracted from addOverlays.ts as HI-1 batch 7 round 3 (2026-05-24).
// Pure mechanical move — source spec, paint expressions, layer ids,
// minzoom, and layer order are byte-identical to the pre-extraction form.
//
// applyState reads visibility from useUiStore so a basemap setStyle()
// rebuild restores the user's current toggle state transparently.

import type { Map as MlMap } from 'maplibre-gl';
import { EMPTY_FC } from '../MapView';
import { registerLayerFamily } from '../layerVisibilityRegistry';
import { useLayerVisibilityStore } from '../../stores/layerVisibilityStore';
import { markOverlayLoaded, refreshOverlay } from '../overlayRuntime';
import { OVERLAY_BY_ID } from '../overlaySpecs';

export function addClassBCDLayers(map: MlMap): void {
  if (!map.getSource('classBCD')) {
    map.addSource('classBCD', { type: 'geojson', data: EMPTY_FC });
    map.addLayer({
      id: 'classBCD-fill',
      type: 'fill',
      source: 'classBCD',
      layout: { visibility: 'none' },
      paint: {
        'fill-color': [
          'match', ['get', 'CLASS'],
          'B', '#1d4ed8',
          'C', '#9d174d',
          'D', '#1e3a5f',
          /* default */ '#374151',
        ],
        'fill-opacity': 0.15,
      },
    });
    map.addLayer({
      id: 'classBCD-line',
      type: 'line',
      source: 'classBCD',
      layout: { visibility: 'none' },
      paint: {
        'line-color': [
          'match', ['get', 'CLASS'],
          'B', '#3b82f6',
          'C', '#ec4899',
          'D', '#6366f1',
          /* default */ '#9ca3af',
        ],
        'line-width': 1.8,
        'line-dasharray': [
          'match', ['get', 'CLASS'],
          'D', ['literal', [4, 3]],
          /* default */ ['literal', [1, 0]],
        ],
      },
    });
    map.addLayer({
      id: 'classBCD-label',
      type: 'symbol',
      source: 'classBCD',
      minzoom: 7,
      layout: {
        visibility: 'none',
        'text-field': [
          'concat',
          'Class ', ['get', 'CLASS'],
          '\n', ['coalesce', ['get', 'NAME_TXT'], ''],
        ],
        'text-size': 9,
        'text-anchor': 'center',
        'text-font': ['Noto Sans Regular'],
      },
      paint: {
        'text-color': [
          'match', ['get', 'CLASS'],
          'B', '#93c5fd',
          'C', '#f9a8d4',
          'D', '#a5b4fc',
          /* default */ '#d1d5db',
        ],
        'text-halo-color': '#0b0d0f',
        'text-halo-width': 1.2,
      },
    });
  }
}

registerLayerFamily({
  id: 'classBCD',
  label: 'Class B/C/D airspace',
  sourceId: 'classBCD',
  layerIds: ['classBCD-fill', 'classBCD-line', 'classBCD-label'],
  mount: (map) => addClassBCDLayers(map),
  visibilitySelector: (l) => l.classBCD,
  applyState: (map) => {
    if (!map.getLayer('classBCD-fill')) return;
    const show = useLayerVisibilityStore.getState().layers.classBCD;
    const v = show ? 'visible' : 'none';
    map.setLayoutProperty('classBCD-fill', 'visibility', v);
    map.setLayoutProperty('classBCD-line', 'visibility', v);
    map.setLayoutProperty('classBCD-label', 'visibility', v);
  },
  onToggleOn: (map) => {
    const spec = OVERLAY_BY_ID.get('classBCD');
    if (!spec) return;
    markOverlayLoaded('classBCD');
    return refreshOverlay(map, spec);
  },
  bboxRefresh: (map) => {
    if (!useLayerVisibilityStore.getState().layers.classBCD) return;
    const spec = OVERLAY_BY_ID.get('classBCD');
    if (!spec) return;
    return refreshOverlay(map, spec);
  },
});
